import { z } from 'zod';

import { indexSearchSchema, listParams } from '@nucleus-commerce/admin-kit/index-table';

import type { AuctionListParams, RegistrationListParams } from '../api/auctions-api';

import { AUCTION_TABS, type AuctionTab } from './auction-rules';

/**
 * URL search params of the auctions list: the status tab (live when absent) and the cursor. The
 * endpoint filters one status at a time, which is what a tab is.
 */
export const auctionListSearchSchema = indexSearchSchema([]).extend({
  status: z.enum(AUCTION_TABS).optional().catch(undefined),
});

export type AuctionListSearch = z.infer<typeof auctionListSearchSchema>;

export function auctionTab(search: Pick<AuctionListSearch, 'status'>): AuctionTab {
  return search.status ?? 'live';
}

export function auctionListParams(search: AuctionListSearch): AuctionListParams {
  return { ...listParams(search), status: auctionTab(search) };
}

export const REGISTRATION_FILTER_FIELDS = [{ key: 'blocked', type: 'boolean' }] as const;

/** URL search params of the bidders list; mirrors GET /auctions/registrations. */
export const registrationListSearchSchema = indexSearchSchema(REGISTRATION_FILTER_FIELDS);

export type RegistrationListSearch = z.infer<typeof registrationListSearchSchema>;

/** `blocked` travels as the string the filter codec writes; the API takes a boolean. */
export function registrationListParams(
  params: Record<string, string | number>,
): RegistrationListParams {
  const { blocked, cursor, limit } = params;
  return {
    ...(typeof cursor === 'string' ? { cursor } : {}),
    ...(typeof limit === 'number' ? { limit } : {}),
    ...(blocked === 'true' || blocked === 'false' ? { blocked: blocked === 'true' } : {}),
  };
}

export function registrationSearchParams(search: RegistrationListSearch): RegistrationListParams {
  return registrationListParams(listParams(search, REGISTRATION_FILTER_FIELDS));
}
